<template>
  <ul class="nav-tree" ref="navTreeRef">
    <li v-for="dir in directories" :key="dir.index" class="chapter-item">
      <div 
        :class="['chapter-head', { 'selected': isSelected(dir.sections[0]) }]" 
        @click="selectChapter(dir)"
      >
        {{ dir.chapter }}
      </div>
      <transition name="fade">
        <ul v-if="isOpen(dir)" class="section-list">
          <li v-for="section in dir.sections" :key="section.index" class="section-item">
            <div 
              :class="['section-head', { 'selected': isSelected(section) }]" 
              @click="selectItem(section)"
              :ref="el => { if (isSelected(section)) selectedItemRef = el }"
            >
              {{ section.title || `Section ${section.index}` }}
            </div>
            <ul v-if="section.clauses" class="clause-list">
              <li v-for="clause in section.clauses" :key="clause.index" class="clause-item">
                <div 
                  :class="['clause-head', { 'selected': isSelected(clause) }]" 
                  @click="selectItem(clause)"
                  :ref="el => { if (isSelected(clause)) selectedItemRef = el }"
                >
                  {{ clause.title }}
                </div>
              </li>
            </ul>
          </li>
        </ul>
      </transition>
    </li>
  </ul>
</template>

<script setup>
import { ref, watch, computed, nextTick } from 'vue';
import { useWindowSize } from '@vueuse/core';

const props = defineProps({
  directories: Array,
  selectedIndex: Number
});

const emit = defineEmits(['select']);

const openChapter = ref(null);
const { width } = useWindowSize();
const isMobile = computed(() => width.value <= 768);
const navTreeRef = ref(null);
const selectedItemRef = ref(null);

const isOpen = (dir) => {
  return openChapter.value === dir.index;
};

const selectChapter = (dir) => {
  const wasOpen = isOpen(dir);
  openChapter.value = wasOpen ? null : dir.index;
  if (!wasOpen && dir.sections.length > 0) {
    selectItem(dir.sections[0]);
  } else {
    nextTick(() => {
      scrollToSelectedItem();
    });
  }
};

const selectItem = (item) => {
  emit('select', item.index);
  nextTick(() => {
    scrollToSelectedItem();
  });
};

const isSelected = (item) => {
  return item.index === props.selectedIndex;
};

const scrollToSelectedItem = () => {
  if (selectedItemRef.value && navTreeRef.value) {
    const container = navTreeRef.value;
    const element = selectedItemRef.value;
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    if (elementRect.bottom > containerRect.bottom) {
      container.scrollTop += elementRect.bottom - containerRect.bottom;
    } else if (elementRect.top < containerRect.top) {
      container.scrollTop -= containerRect.top - elementRect.top;
    }
  }
};

// 监听 selectedIndex 的变化
watch(() => props.selectedIndex, (newIndex) => {
  if (newIndex === undefined) return;
  
  // 找到对应的章节并展开
  const chapter = props.directories.find(dir => 
    dir.sections.some(section => 
      section.index === newIndex || (section.clauses && section.clauses.some(clause => clause.index === newIndex))
    )
  );
  if (chapter) {
    openChapter.value = chapter.index;
    nextTick(() => {
      scrollToSelectedItem();
    });
  }
});

// 初始化时展开第一个章节
watch(() => props.directories, (newDirectories) => {
  if (newDirectories.length > 0) {
    openChapter.value = newDirectories[0].index;
    if (isMobile.value && newDirectories[0].sections.length > 0) {
      selectItem(newDirectories[0].sections[0]);
    }
  }
}, { immediate: true });

// 监听屏幕宽度变化
watch(isMobile, (newIsMobile) => {
  if (newIsMobile && props.directories.length > 0) {
    // 在切换到移动端时，如果没有选中的项，则选择第一个章节的第一个部分
    if (props.selectedIndex === undefined) {
      const firstChapter = props.directories[0];
      if (firstChapter.sections.length > 0) {
        selectItem(firstChapter.sections[0]);
      }
    }
  }
});
</script>

<style scoped>
.nav-tree {
  list-style-type: none;
  padding-left: 0;
  font-family: var(--dca-font);
  color: var(--dca-text);
  font-size: 16px;
  background-color: var(--dca-surface);
}
.chapter-head, .section-head, .clause-head {
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid var(--dca-border);
  color: var(--dca-text);
}
.chapter-head {
  font-weight: bold;
  background-color: var(--dca-surface-2);
  color: var(--dca-text);
  font-size: 18px;
  padding: 12px 16px;
}
.section-head {
  background-color: var(--dca-surface);
  color: var(--dca-text-muted);
  font-size: 16px;
  padding: 8px 16px 8px 24px;
}
.clause-head {
  background-color: var(--dca-surface);
  color: var(--dca-text-muted);
  font-size: 14px;
  padding: 6px 16px 6px 32px;
}
.chapter-head:hover, .section-head:hover, .clause-head:hover {
  background-color: var(--dca-accent-soft);
  color: var(--dca-accent);
}
.section-list, .clause-list {
  padding-left: 0;
  margin: 0;
}
.section-item, .clause-item {
  margin-top: 1px; /* 添加一个很小的上边距 */
}
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
/* 添加选中状态的样式 */
.section-head.selected,
.clause-head.selected {
  background-color: var(--dca-accent-soft);
  color: var(--dca-accent);
  font-weight: bold;
}
</style>
